import { aliasKey, containsTerm } from './debug-data-terms';

export function buildIdentityAliases(composedMap, entity) {
  const idKeyAliases = new Set();
  const compositeIdKeyTuples = [];
  const recordActions = [];

  for (const [action, entry] of Object.entries(composedMap)) {
    if (entry.entity !== entity || entry.identity !== 'record') continue;
    recordActions.push(action);
    for (const alias of entry.idKeys ?? []) idKeyAliases.add(aliasKey(alias));
    if (entry.compositeIdKeys?.length) compositeIdKeyTuples.push(entry.compositeIdKeys);
  }

  return { idKeyAliases: [...idKeyAliases], compositeIdKeyTuples, recordActions };
}

export function buildIdentityTerm({ composedMap, identity }) {
  const { idKeyAliases, compositeIdKeyTuples, recordActions } = buildIdentityAliases(composedMap, identity.entity);

  return { _and: [{ action: { _in: recordActions } }, ...keyTerms({ identity, idKeyAliases, compositeIdKeyTuples })] };
}

// Containment alone cannot say whether a matched row IS the record or merely REFERENCES it: the
// same key name serves both roles -- pipelineId is pipeline's own id key and pipelineTag's and
// AJS_FILE_BUILT's parent reference. The action bound is what separates them, and it is derived
// from the map's own record entries so it cannot drift from them.
function keyTerms({ identity, idKeyAliases, compositeIdKeyTuples }) {
  if (identity.source === 'idKeys') {
    return [{ _or: idKeyAliases.map(key => containsTerm(key, identity.recordId)) }];
  }

  // compositeIdKeys: one term per position. A position's term is an _or across every key name used
  // at that tuple position by any composedMap entry for this entity, because a future legacy
  // alternate could spell one member differently while the tuple's arity and position order stay the
  // entity's own, stable shape.
  const arity = identity.recordId.length;
  const positionTerms = [];
  for (let position = 0; position < arity; position += 1) {
    const keysAtPosition = new Set(
      compositeIdKeyTuples.filter(tuple => tuple.length === arity).map(tuple => tuple[position])
    );
    positionTerms.push({
      _or: [...keysAtPosition].map(key => containsTerm(key, identity.recordId[position])),
    });
  }
  return positionTerms;
}
